import React, { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { Helmet } from "react-helmet-async";

const Contact = () => {
  const form = useRef();
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [verificationCode, setVerificationCode] = useState("");
  const [enteredCode, setEnteredCode] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const sendVerificationCode = (e) => {
    e.preventDefault();

    if (!formData.email) {
      alert("Please enter your email first.");
      return;
    }

    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setVerificationCode(code);
    setIsSendingCode(true);

    setTimeout(() => {
      setIsSendingCode(false);
      setShowVerification(true); // Show verification input fields
    }, 2000);

    const templateParams = {
      email: formData.email,
      verification_code: code,
    };

    emailjs
      .send("service_j4ghqeg", "template_qxvstpl", templateParams, {
        publicKey: "iR_kUKQEC0rRRsvzh",
      })
      .then(() => {
        alert("Verification code sent to your email.");
      })
      .catch((error) => {
        console.error("Failed to send verification code:", error);
        alert("Failed to send verification code.");
      });
  };

  const verifyCode = () => {
    if (enteredCode === verificationCode) {
      alert("Email verified successfully!");
      setIsVerified(true);
    } else {
      alert("Invalid verification code. Please try again.");
    }
  };

  const sendEmail = (e) => {
    e.preventDefault();

    if (!isVerified) {
      alert("Please verify your email before sending the message.");
      return;
    }

    emailjs
      .sendForm("service_j4ghqeg", "template_0czk6y5", form.current, {
        publicKey: "iR_kUKQEC0rRRsvzh",
      })
      .then(() => {
        alert("Message sent successfully!");
        setFormData({ name: "", email: "", message: "" });
        setIsVerified(false);
        setShowVerification(false);
        setEnteredCode("");
      })
      .catch((error) => {
        console.error("Failed to send message:", error);
        alert("Failed to send message. Please try again.");
      });
  };

  return (
    <div id="contact" className="container mx-auto p-10 m-4 grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Meta Tags */}
      <Helmet>
        <title>Contact Dr. Srikanth | Best Urologist in Bangalore</title>
        <meta name="description" content="Get in touch with Dr. Srikanth, a leading urologist in Bangalore. Book an appointment for expert kidney stone treatment, prostate care, and more." />
        <meta name="keywords" content="urologist in Bangalore, best urologist, kidney stone treatment, prostate specialist, Dr. Srikanth contact" />
        <link rel="canonical" href="https://drsrikanthurologist.com/contact" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Urologist",
            "name": "Dr. Srikanth",
            "url": "https://drsrikanthurologist.com",
            "address": {
              "@type": "560076",
              "addressLocality": "Bangalore",
              "addressCountry": "IN"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-6366017702",
              "contactType": "medical service"
            }
          })}
        </script>
      </Helmet>

      <div className="w-full h-80 md:h-full">
        <iframe
          className="w-full h-full rounded-lg shadow-md"
          src="https://maps.google.com/maps?width=600&amp;height=400&amp;hl=en&amp;q=Dr.srikanth%20urology%20btm%20layout&amp;t=&amp;z=14&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
          allowFullScreen=""
          loading="lazy"
        ></iframe>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md flex flex-col items-center">
        <h3 className="text-2xl font-semibold text-blue-700 mb-4">Contact Us</h3>
        <form ref={form} onSubmit={sendEmail} className="space-y-4 w-full">
          <div>
            <label className="block mb-1" htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md"
              required
            />
          </div>
          <div>
            <label className="block mb-1" htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md"
              required
            />
          </div>
          <div>
            <label className="block mb-1" htmlFor="message">Message</label>
            <input
              type="text"
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md"
              required
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={sendVerificationCode}
              className={`p-2 text-white rounded-md ${isSendingCode ? "bg-gray-400 cursor-not-allowed" : "bg-blue-500"}`}
              disabled={isSendingCode}
            >
              {isSendingCode ? "Sending..." : "Send Verification Code"}
            </button>
          </div>

          {showVerification && (
            <div>
              <label className="block mb-1" htmlFor="verificationCode">Enter Verification Code</label>
              <input
                type="text"
                id="verificationCode"
                value={enteredCode}
                onChange={(e) => setEnteredCode(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
              <button
                type="button"
                onClick={verifyCode}
                className="mt-2 p-2 bg-green-500 text-white rounded-md"
              >
                Verify Code
              </button>
            </div>
          )}

          <button
            type="submit"
            className={`w-full p-2 text-white rounded-md ${isVerified ? "bg-teal-600" : "bg-gray-400 cursor-not-allowed"}`}
            disabled={!isVerified}
          >
            Send Message
          </button>
        </form>
      </div>
    </div>
  );
};

export default Contact;
